import React from 'react';
import { Box, Typography } from '@mui/material';
import { CustomChip } from '../CustomChip';
import { TrainingStatus } from '../../redux/learnersSlice';
import './AchievementsCard.css';
import { handleErrorImg, parseImgURLBadge, roundUpPercentage, buildLockedBadgeProgressText, getBadgeDescription } from '../../utils/util';
import { formatUnlockedOn } from '../../utils/dateTimeHelper';
import { EBadgeStatus } from '../../constants/enums';
import { images } from '../../constants/images';

const AchievementsCard = ({ badge }: any) => {
  const lockedIconsByRarity: Record<string, string> = {
    Bronze: images.BronzeLockedIcon,
    Silver: images.SilverLockedIcon,
    Gold: images.GoldLockedIcon,
    Diamond: images.DiamondLockedIcon,
    Emerald: images.EmeraldLockedIcon,
    Opal: images.OpalLockedIcon
  };
  const getCourseChipStyle = (status: TrainingStatus) => {
    const getStyle = (primaryColor: string, secondaryColor: string, opacity: string) => ({
      fontWeight: '500',
      backgroundColor: primaryColor,
      float: 'right',
      border: `1px solid ${secondaryColor}`,
      '.MuiSvgIcon-root': {
        color: secondaryColor
      },
      '.MuiChip-deleteIcon:hover': {
        color: secondaryColor
      },
      height: 'auto',
      '& .MuiChip-label': {
        color: '#000000',
        display: 'block',
        whiteSpace: 'normal',
        opacity: opacity,
        minWidth: '170px',
        textAlign: 'center'
      }
    });

    switch (status) {
      case 'Completed':
        return getStyle('#EAF7C6', '#A2BC30', '0.9');
      case 'Failed':
        return getStyle('#E6E7E7', '#B3B8B8', '0.6');
      // case 'Incomplete':
      //   return getStyle('#FFECB3', '#FFB300');
      // case 'Not Started':
      //   return getStyle('#F7F7F7', '#677070');
      default:
        return null;
    }
  };
  const imageStyle = { height: 76, width: 76 };
  const formatUnlockedDate = formatUnlockedOn(badge.unlocked_at); // Format date to be "D MMM YYYY (e.g. 10 Sep 2025)"
  const lockedBadgeProgressText = buildLockedBadgeProgressText(
    badge.badgeId,
    badge.current_count,
    badge.required_count,
    badge.vector_counts,
    badge.vector_requirements
  );
  const badgeDescription = getBadgeDescription(badge.badgeId, badge.status);
  const imgSrc = parseImgURLBadge(badge.category, badge.badgeId);
  const lockedBadgeImgSrc = (rarity?: string) =>
    (rarity && lockedIconsByRarity[rarity]) || images.CourseNonThumbnail;
  const badgeImgSrc =
    badge.status === EBadgeStatus.unlocked ? imgSrc : lockedBadgeImgSrc(badge.rarity);
  const roundUpProgress = roundUpPercentage(badge?.peer_percentage ?? 0);
  return (
    <Box sx={{ mx: 'auto', mb: '20px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          borderRadius: '12px',
          mb: 2,
          overflow: 'hidden',
          border: '1px solid #e0e0e0',
          cursor: 'pointer',
          height: '150px'
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#193051',
            width: '100px',
            justifyContent: 'center'
          }}>
          <img
            style={imageStyle}
            src={badgeImgSrc}
            alt={`badge-${badge ?? 'unknown'}`}
            onError={handleErrorImg}
            loading="lazy"
            decoding="async"
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            p: '20px',
            py: '23.5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background:
              badge.status === EBadgeStatus.locked
                ? `linear-gradient(to right, #e0f7fa ${badge.progress_percentage}%, #ffffff ${badge.progress_percentage}%)`
                : ''
          }}>
          {/* Content */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              alignItems: 'center',
              columnGap: '10px',
              width: '100%'
            }}>
            <Box>
              <Typography
                sx={{
                  fontWeight: '600',
                  fontSize: '18px',
                  opacity: '0.9',
                  lineHeight: '22px'
                }}>
                {badge.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: '18px',
                  fontWeight: '500',
                  fontSize: '16px',
                  opacity: '0.6',
                 
                  minHeight: '40px',
                  alignContent: 'center',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                {badgeDescription}
              </Typography>
              {badge.status === EBadgeStatus.locked ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                    <Typography
                      sx={{
                        fontFamily: 'Montserrat',
                        fontWeight: 500,
                        fontSize: '14px',
                        opacity: 0.6,
                        color: '#000000',
                        position: 'relative', 
                        top: '4px'
                      }}>
                      {lockedBadgeProgressText}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                    <Typography
                      style={{
                        fontFamily: 'Montserrat',
                        fontWeight: 500,
                        fontSize: '14px',
                        opacity: 0.6,
                        color: '#000000',
                        position: 'relative', 
                        top: '4px'
                      }}>
                      Unlocked on{' '}
                      <span
                        style={{
                          fontFamily: 'Montserrat',
                          fontWeight: '600'
                        }}>
                        {formatUnlockedDate}
                      </span>
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
            <Box sx={{ alignSelf: 'center', mb: 1 }}>
              {badge.status === EBadgeStatus.locked ? (
                <CustomChip
                  label={`${roundUpProgress}% have unlocked this.`}
                  sx={getCourseChipStyle('Failed')}
                  className="custom-chip"
                />
              ) : (
                <CustomChip
                  label={`${roundUpProgress}% have unlocked this.`}
                  sx={getCourseChipStyle('Completed')}
                  className="custom-chip"
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AchievementsCard;
